// ChargeReviewDetail.jsx — 관리자용 직원 1:1급여 상세 (2026-10-06 UX)
//   위에서 아래로: 머리(이름·담당·근무일) → 확정된 달 띠 → 점검·참고 → 확인 필요 [넣기][빼기] → 다음 직원 →
//   계산 풀이(영수증) → 1:1급여에 들어가는 계약·취소 전체 → 자세히(상품별 장수·연차) → [급여 조건][실적 요약표]
//   마케터가 정산서에서 보는 MonthlyCalculationDetail 은 그대로 두고 관리자 화면만 이 부품을 쓴다.
import React, { useState } from 'react';
import { ConsultantMonthlySummaryModal } from './ConsultantMonthlySummaryModal';
import { EventList } from './EventList';
import { ChargeReceipt } from './ChargeReceipt';
import { PendingDecisions } from './PendingDecisions';
import { classifyWarnings, nextPendingId, rowPendingCount } from './chargeModel';
import { fmtDateRange, fmtNum } from './monthlyCalculationUtils';
import { C } from './charge.styles';

function DetailRows({ rows, emptyText }) {
  if (!rows.length) return <div style={C.sectionHint}>{emptyText}</div>;
  return rows.map((item, index) => (
    <div key={`${item.label}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, color: 'var(--text-sub)' }}>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{item.label}</span>
      <strong style={{ whiteSpace: 'nowrap' }}>{item.value}</strong>
    </div>
  ));
}

// touch: 휴대폰 — 계약 목록의 처리 칸을 손가락 크기(40px)로
export function ChargeReviewDetail({ calc, row, assignment, rows, onSelect, onOpenRules, integratedMenuId = null, touch = false }) {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  if (!row) return <div style={C.empty}>직원을 고르면 계산 풀이가 보여요.</div>;

  const pending = rowPendingCount(row);
  const { checks, notes } = classifyWarnings(row);
  const nextId = nextPendingId(rows, row.assignmentId);
  const decide = (event, decisionStatus) => calc.handleDecisionChange({
    assignmentId: row.assignmentId,
    contractId: event.contractId,
    eventType: event.type,
    decisionStatus,
  });
  const products = (row.productBreakdown || []).map((item) => ({
    label: `${item.name} · 계약 ${fmtNum(item.contractQty)} / 취소 ${fmtNum(item.cancelledQty)}`,
    value: `${fmtNum(item.weightedQty)}장`,
  }));
  const leaves = (row.leaveDetails || []).map((item) => ({
    label: `${item.date}${item.dateTo ? `~${item.dateTo}` : ''} ${item.name}`,
    value: `${fmtNum(item.count)}일`,
  }));

  return (
    <div style={C.detail}>
      <div style={C.detailHead}>
        <div style={{ minWidth: 0 }}>
          <div style={C.detailName}>{row.consultantName}</div>
          <div style={C.detailSub}>
            담당 마케터 {row.marketerName || '-'} · {fmtDateRange(row.activeStartDate, row.activeEndDate)} 중 {row.activeDays}/{row.monthDays}일 근무
          </div>
        </div>
      </div>

      {calc.isConfirmed && (
        <div style={C.lockBand}>확정된 달이라 바꿀 수 없어요. 고치려면 위에서 '확정 취소'를 눌러 주세요.</div>
      )}

      {(checks.length > 0 || notes.length > 0) && (
        <div style={C.chips}>
          {checks.map((text) => <span key={`c-${text}`} style={C.chip('danger')}>점검 · {text}</span>)}
          {notes.map((text) => <span key={`n-${text}`} style={C.chip('muted')}>참고 · {text}</span>)}
        </div>
      )}

      {!calc.isConfirmed && <PendingDecisions row={row} disabled={calc.loading} onDecide={decide} />}

      {!calc.isConfirmed && pending === 0 && nextId && (
        <div style={{ ...C.section, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span style={C.sectionHint}>이 직원은 정리가 끝났어요.</span>
          <button type="button" style={C.outlineBtn} onClick={() => onSelect(nextId)}>다음 확인 필요 직원 →</button>
        </div>
      )}

      <ChargeReceipt row={row} />

      <div style={C.section}>
        <div style={C.sectionTitle}>1:1급여에 들어가는 계약·취소</div>
        <EventList
          events={row.events || []}
          readOnly={calc.isConfirmed}
          onDecisionChange={calc.isConfirmed ? null : (event, status) => decide(event, status)}
          touch={touch}
        />
        <button type="button" style={{ ...C.smallLinkBtn, alignSelf: 'flex-start' }} onClick={() => setShowDetails((v) => !v)}>
          {showDetails ? '자세히 접기' : '자세히 보기 (상품별 장수·연차 내역)'}
        </button>
        {showDetails && (
          <>
            <div style={C.sectionHint}>상품별 장수 · 인정 {fmtNum(row.weightedCount)}장</div>
            <DetailRows rows={products} emptyText="계약·취소 실적 없음" />
            <div style={C.sectionHint}>연차 내역 · {fmtNum(row.leaveDays)}일</div>
            <DetailRows rows={leaves} emptyText="연차 없음" />
          </>
        )}
      </div>

      <div style={C.footBtns}>
        <button type="button" style={C.outlineBtn} onClick={() => onOpenRules(assignment)} disabled={!assignment}>급여 조건</button>
        <button type="button" style={C.outlineBtn} onClick={() => setSummaryOpen(true)}>실적 요약표</button>
      </div>

      {summaryOpen && (
        <ConsultantMonthlySummaryModal
          row={row}
          year={calc.parsedMonth.year}
          month={calc.parsedMonth.month}
          scope="admin"
          integratedMenuId={integratedMenuId}
          onClose={() => setSummaryOpen(false)}
        />
      )}
    </div>
  );
}
